/**
 * AdminDashboard.jsx
 * ══════════════════
 * Founder/admin-only platform health dashboard.
 *
 * Shows:
 *   – DAU / WAU / MAU with DAU:MAU ratio
 *   – Total users, projects, forks, challenges
 *   – 30-day signup trend (sparkline)
 *   – 30-day daily active users trend (sparkline)
 *   – 30-day project creation trend
 *   – Cross-zone forks this month (the magic metric)
 *   – Zone breakdown bar chart
 *   – Recent signups table
 *   – Top creators table
 *
 * Access: admin role required (gated server-side + client-side redirect)
 * Route:  /admin  (add to App.jsx — see bottom of this file)
 *
 * Place at: src/pages/AdminDashboard.jsx
 *
 * ─── App.jsx additions ───────────────────────────────────────────────────
 * 1. Import:
 *      import AdminDashboard from './pages/AdminDashboard';
 *
 * 2. Add route (inside <Routes>, after /settings):
 *      <Route path="/admin" element={
 *        <ProtectedRoute>
 *          <AppLayout><AdminDashboard /></AppLayout>
 *        </ProtectedRoute>
 *      } />
 *
 * 3. Add "Admin" link to DesktopNav user menu (only show if user.role === 'admin'):
 *      {user?.isAdmin && (
 *        <button onClick={() => { navigate('/admin'); setShowUserMenu(false); }}
 *          className="w-full flex items-center gap-2 px-4 py-3 text-sm text-zinc-300 hover:bg-zinc-800 transition-colors">
 *          <LayoutDashboard className="w-4 h-4" />
 *          Admin Dashboard
 *        </button>
 *      )}
 * ─────────────────────────────────────────────────────────────────────────
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, FolderGit2, GitFork, Target, TrendingUp, TrendingDown,
  Minus, RefreshCw, Shield, Activity, Zap, Eye, Calendar,
  AlertCircle, CheckCircle, ChevronUp, ChevronDown, Search,
  ArrowUpRight, BarChart3, Sparkles, Clock,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';

// ============================================================================
// TINY SPARKLINE — pure SVG, no chart library dependency
// ============================================================================

const Sparkline = ({ data = [], color = '#a855f7', height = 40, className = '' }) => {
  if (!data.length) return null;

  const values = data.map(d => d.count);
  const max = Math.max(...values, 1);
  const min = Math.min(...values);
  const range = max - min || 1;
  const w = 200;
  const h = height;
  const pad = 2;

  const points = values.map((v, i) => {
    const x = pad + (i / Math.max(values.length - 1, 1)) * (w - pad * 2);
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return `${x},${y}`;
  }).join(' ');

  const fillPoints = `${pad},${h} ` + points + ` ${w - pad},${h}`;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className={`w-full ${className}`}
      style={{ height }}
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id={`grad-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.03" />
        </linearGradient>
      </defs>
      <polygon
        points={fillPoints}
        fill={`url(#grad-${color.replace('#', '')})`}
      />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// ============================================================================
// MINI BAR CHART — for zone breakdown
// ============================================================================

const ZoneBar = ({ zone, count, max, color }) => {
  const pct = max > 0 ? (count / max) * 100 : 0;
  const ZONE_COLORS = {
    build: '#f59e0b',
    beautiful: '#ec4899',
    sound: '#06b6d4',
    taste: '#f97316',
    story: '#8b5cf6',
    heal: '#ef4444',
    smart: '#6366f1',
    sell: '#22c55e',
    unknown: '#71717a',
  };
  const barColor = ZONE_COLORS[zone] || '#71717a';

  const ZONE_LABELS = {
    build: 'Build Something',
    beautiful: 'Make It Beautiful',
    sound: 'Make It Sound Good',
    taste: 'Make It Taste Good',
    story: 'Spin a Tale',
    heal: 'Patch People Up',
    smart: 'Make It Smart',
    sell: 'Sell It',
    unknown: 'Unclassified',
  };

  return (
    <div className="flex items-center gap-3 group">
      <div className="w-28 text-xs text-zinc-400 truncate group-hover:text-zinc-200 transition-colors">
        {ZONE_LABELS[zone] || zone}
      </div>
      <div className="flex-1 bg-zinc-800/60 rounded-full h-1.5 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, backgroundColor: barColor }}
        />
      </div>
      <div className="w-8 text-xs text-zinc-400 text-right tabular-nums">{count}</div>
    </div>
  );
};

// ============================================================================
// STAT CARD
// ============================================================================

const StatCard = ({ label, value, sub, trend, icon: Icon, color, series, loading }) => {
  const trendUp = trend > 0;
  const trendFlat = trend === 0;

  return (
    <div className="relative bg-zinc-900/60 border border-zinc-800/60 rounded-2xl p-5 overflow-hidden group hover:border-zinc-700/60 transition-all duration-300">
      {/* subtle ambient glow */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: `radial-gradient(circle at 30% 50%, ${color}08 0%, transparent 70%)`,
        }}
      />

      <div className="relative">
        {/* Header row */}
        <div className="flex items-start justify-between mb-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${color}18`, border: `1px solid ${color}25` }}
          >
            {loading ? (
              <div className="w-4 h-4 rounded-full bg-zinc-700 animate-pulse" />
            ) : (
              <Icon className="w-4 h-4" style={{ color }} />
            )}
          </div>

          {trend !== undefined && !loading && (
            <div className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
              trendFlat
                ? 'text-zinc-500 bg-zinc-800'
                : trendUp
                  ? 'text-emerald-400 bg-emerald-400/10'
                  : 'text-red-400 bg-red-400/10'
            }`}>
              {trendFlat ? <Minus className="w-3 h-3" /> : trendUp ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {Math.abs(trend)}%
            </div>
          )}
        </div>

        {/* Value */}
        {loading ? (
          <div className="space-y-1.5 mb-1">
            <div className="h-7 w-24 bg-zinc-800 rounded-lg animate-pulse" />
            <div className="h-3 w-16 bg-zinc-800/60 rounded animate-pulse" />
          </div>
        ) : (
          <>
            <div className="text-2xl font-bold text-white tabular-nums tracking-tight">
              {typeof value === 'number' ? value.toLocaleString() : value}
            </div>
            <div className="text-xs text-zinc-500 mt-0.5">{label}</div>
            {sub && <div className="text-xs text-zinc-600 mt-0.5">{sub}</div>}
          </>
        )}

        {/* Sparkline */}
        {series && series.length > 0 && !loading && (
          <div className="mt-3 -mx-1">
            <Sparkline data={series} color={color} height={32} />
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// USERS TABLE
// ============================================================================

const UsersTable = ({ users = [], loading }) => {
  const navigate = useNavigate();

  const STATUS_COLORS = {
    active: 'text-emerald-400 bg-emerald-400/10',
    suspended: 'text-amber-400 bg-amber-400/10',
    banned: 'text-red-400 bg-red-400/10',
  };

  const timeAgo = (iso) => {
    if (!iso) return '—';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  if (loading) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-12 bg-zinc-800/40 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-800/60">
            <th className="text-left text-xs text-zinc-500 font-medium pb-2 pr-4">User</th>
            <th className="text-left text-xs text-zinc-500 font-medium pb-2 pr-4">Joined</th>
            <th className="text-left text-xs text-zinc-500 font-medium pb-2 pr-4">Last Login</th>
            <th className="text-right text-xs text-zinc-500 font-medium pb-2 pr-4">Projects</th>
            <th className="text-left text-xs text-zinc-500 font-medium pb-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr
              key={u.id}
              className="border-b border-zinc-800/30 hover:bg-zinc-800/30 transition-colors cursor-pointer"
              onClick={() => navigate(`/profile/${u.id}`)}
            >
              <td className="py-2.5 pr-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-xs font-bold text-white shrink-0">
                    {u.username?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <div className="text-zinc-200 font-medium leading-tight">@{u.username}</div>
                    <div className="text-zinc-600 text-xs truncate max-w-[140px]">{u.email}</div>
                  </div>
                </div>
              </td>
              <td className="py-2.5 pr-4 text-zinc-500 text-xs">{timeAgo(u.createdAt)}</td>
              <td className="py-2.5 pr-4 text-zinc-500 text-xs">{timeAgo(u.lastLogin)}</td>
              <td className="py-2.5 pr-4 text-right text-zinc-400 tabular-nums">{u.projectCount}</td>
              <td className="py-2.5">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[u.moderationStatus] || STATUS_COLORS.active}`}>
                  {u.moderationStatus || 'active'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [windowDays, setWindowDays] = useState(30);
  const [dbHealth, setDbHealth] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [userList, setUserList] = useState(null);
  const [userListLoading, setUserListLoading] = useState(false);
  const lastFetch = useRef(null);

  // ── Fetch main stats ───────────────────────────────────────────────────
  const fetchStats = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError(null);

    try {
      const res = await api.get(`/admin/stats?days=${windowDays}`);
      setStats(res.data);
      lastFetch.current = new Date();
    } catch (err) {
      if (err?.response?.status === 403) {
        navigate('/dashboard');
        return;
      }
      setError(err?.response?.data?.error || 'Failed to load admin stats');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [windowDays, navigate]);

  // ── Fetch health ───────────────────────────────────────────────────────
  const fetchHealth = useCallback(async () => {
    try {
      const res = await api.get('/admin/health');
      setDbHealth(res.data);
    } catch {
      setDbHealth({ status: 'degraded', database: 'error' });
    }
  }, []);

  // ── Fetch user list ────────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    setUserListLoading(true);
    try {
      const params = new URLSearchParams({ page: userPage, per_page: 15 });
      if (userSearch) params.set('q', userSearch);
      const res = await api.get(`/admin/users?${params}`);
      setUserList(res.data);
    } catch {
      // silently fail — user table is secondary
    } finally {
      setUserListLoading(false);
    }
  }, [userPage, userSearch]);

  useEffect(() => { fetchStats(); fetchHealth(); }, [fetchStats, fetchHealth]);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Refresh every 5 minutes silently
  useEffect(() => {
    const id = setInterval(() => fetchStats(true), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchStats]);

  // ── Error state ────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-8">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-white font-semibold text-lg mb-2">Failed to load dashboard</h2>
          <p className="text-zinc-400 text-sm mb-6">{error}</p>
          <button
            onClick={() => fetchStats()}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-sm transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const t = stats?.totals || {};
  const eng = stats?.engagement || {};
  const nu = stats?.newUsers || {};

  const dauMauPct = eng.mau > 0 ? Math.round((eng.dau / eng.mau) * 100) : 0;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ═══ HEADER ═══ */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
                <BarChart3 className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-xl font-bold text-white tracking-tight">Admin Dashboard</h1>
              {dbHealth && (
                <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                  dbHealth.status === 'healthy'
                    ? 'text-emerald-400 bg-emerald-400/10 border border-emerald-400/20'
                    : 'text-red-400 bg-red-400/10 border border-red-400/20'
                }`}>
                  {dbHealth.status === 'healthy'
                    ? <CheckCircle className="w-3 h-3" />
                    : <AlertCircle className="w-3 h-3" />}
                  {dbHealth.status}
                </span>
              )}
            </div>
            <p className="text-zinc-500 text-sm">
              {lastFetch.current
                ? `Last refreshed ${lastFetch.current.toLocaleTimeString()}`
                : 'Loading platform metrics…'}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Window selector */}
            <div className="flex bg-zinc-900 border border-zinc-800 rounded-xl p-1 gap-1">
              {[7, 30, 90].map(d => (
                <button
                  key={d}
                  onClick={() => setWindowDays(d)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                    windowDays === d
                      ? 'bg-purple-600 text-white shadow-sm'
                      : 'text-zinc-400 hover:text-zinc-200'
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>

            <button
              onClick={() => fetchStats(true)}
              disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-2 bg-zinc-900 border border-zinc-800 hover:border-zinc-700 text-zinc-400 hover:text-zinc-200 rounded-xl text-xs font-medium transition-all"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* ═══ TOP STAT CARDS ═══ */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <StatCard
            label="Total Users"
            value={t.users}
            sub={`+${nu.thisMonth || 0} this month`}
            trend={nu.trend}
            icon={Users}
            color="#a855f7"
            series={stats?.signupSeries}
            loading={loading}
          />
          <StatCard
            label="Total Projects"
            value={t.projects}
            icon={FolderGit2}
            color="#6366f1"
            series={stats?.projectSeries}
            loading={loading}
          />
          <StatCard
            label="Total Forks"
            value={t.forks}
            icon={GitFork}
            color="#06b6d4"
            loading={loading}
          />
          <StatCard
            label="Challenges"
            value={t.challenges}
            icon={Target}
            color="#f59e0b"
            loading={loading}
          />
        </div>

        {/* ═══ ENGAGEMENT ROW ═══ */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <StatCard
            label="Daily Active Users"
            value={eng.dau}
            sub={`${dauMauPct}% of MAU`}
            icon={Activity}
            color="#22c55e"
            series={stats?.dauSeries}
            loading={loading}
          />
          <StatCard
            label="Weekly Active Users"
            value={eng.wau}
            icon={Calendar}
            color="#10b981"
            loading={loading}
          />
          <StatCard
            label="Monthly Active Users"
            value={eng.mau}
            icon={Users}
            color="#14b8a6"
            loading={loading}
          />
          <StatCard
            label="Cross-Zone Forks"
            value={stats?.crossZoneForks}
            sub="last 30 days"
            icon={Sparkles}
            color="#f97316"
            loading={loading}
          />
        </div>

        {/* ═══ NEW USERS TODAY / WEEK ═══ */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { label: 'New Today',      value: nu.today,     icon: Zap,          color: '#a855f7' },
            { label: 'New This Week',  value: nu.thisWeek,  icon: TrendingUp,   color: '#6366f1' },
            { label: 'New This Month', value: nu.thisMonth, icon: ArrowUpRight, color: '#06b6d4' },
          ].map(s => (
            <StatCard key={s.label} {...s} loading={loading} />
          ))}
        </div>

        {/* ═══ CHARTS ROW ═══ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">

          {/* Signup trend chart */}
          <div className="lg:col-span-2 bg-zinc-900/60 border border-zinc-800/60 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-zinc-200">New Signups</h3>
                <p className="text-xs text-zinc-500">{windowDays}-day trend</p>
              </div>
              {!loading && (
                <div className="text-xs text-zinc-500 tabular-nums">
                  Total: {stats?.signupSeries?.reduce((s, d) => s + d.count, 0) || 0}
                </div>
              )}
            </div>
            {loading ? (
              <div className="h-32 bg-zinc-800/40 rounded-xl animate-pulse" />
            ) : (
              <SignupLineChart data={stats?.signupSeries || []} />
            )}
          </div>

          {/* Zone breakdown */}
          <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-2xl p-5">
            <h3 className="text-sm font-semibold text-zinc-200 mb-1">Projects by Zone</h3>
            <p className="text-xs text-zinc-500 mb-4">All time</p>
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-3 bg-zinc-800/40 rounded animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="space-y-2.5">
                {(stats?.zoneBreakdown || []).slice(0, 8).map(z => (
                  <ZoneBar
                    key={z.zone}
                    zone={z.zone}
                    count={z.count}
                    max={stats.zoneBreakdown[0]?.count || 1}
                  />
                ))}
                {(!stats?.zoneBreakdown?.length) && (
                  <p className="text-zinc-600 text-xs">No zone data yet</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ═══ DAU CHART ═══ */}
        <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-2xl p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-zinc-200">Daily Active Users</h3>
              <p className="text-xs text-zinc-500">{windowDays}-day trend — unique logins per day</p>
            </div>
          </div>
          {loading ? (
            <div className="h-32 bg-zinc-800/40 rounded-xl animate-pulse" />
          ) : (
            <DauLineChart data={stats?.dauSeries || []} />
          )}
        </div>

        {/* ═══ TABLES ROW ═══ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

          {/* Recent signups */}
          <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-zinc-200">Recent Signups</h3>
                <p className="text-xs text-zinc-500">Last 10 new users</p>
              </div>
            </div>
            <UsersTable users={stats?.recentSignups || []} loading={loading} />
          </div>

          {/* Top creators */}
          <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-zinc-200">Top Creators</h3>
                <p className="text-xs text-zinc-500">By project count, all time</p>
              </div>
            </div>
            {loading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-12 bg-zinc-800/40 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-800/60">
                      <th className="text-left text-xs text-zinc-500 font-medium pb-2 pr-4">Creator</th>
                      <th className="text-right text-xs text-zinc-500 font-medium pb-2 pr-2">Projects</th>
                      <th className="text-right text-xs text-zinc-500 font-medium pb-2 pr-2">Views</th>
                      <th className="text-right text-xs text-zinc-500 font-medium pb-2">Forks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(stats?.topCreators || []).map((c, i) => (
                      <tr key={c.id} className="border-b border-zinc-800/30 hover:bg-zinc-800/30 transition-colors">
                        <td className="py-2.5 pr-4">
                          <div className="flex items-center gap-2">
                            <span className="text-zinc-600 text-xs w-4 tabular-nums">{i + 1}</span>
                            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-xs font-bold text-white">
                              {c.username?.[0]?.toUpperCase()}
                            </div>
                            <span className="text-zinc-200 text-xs">@{c.username}</span>
                          </div>
                        </td>
                        <td className="py-2.5 pr-2 text-right text-zinc-400 tabular-nums text-xs">{c.projectCount}</td>
                        <td className="py-2.5 pr-2 text-right text-zinc-500 tabular-nums text-xs">{c.totalViews.toLocaleString()}</td>
                        <td className="py-2.5 text-right text-zinc-500 tabular-nums text-xs">{c.totalForks}</td>
                      </tr>
                    ))}
                    {!stats?.topCreators?.length && (
                      <tr><td colSpan={4} className="py-4 text-zinc-600 text-xs text-center">No data yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* ═══ FULL USER LIST ═══ */}
        <div className="mt-4 bg-zinc-900/60 border border-zinc-800/60 rounded-2xl p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="text-sm font-semibold text-zinc-200">All Users</h3>
              <p className="text-xs text-zinc-500">
                {userList ? `${userList.total.toLocaleString()} total` : 'Loading…'}
              </p>
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
              <input
                type="text"
                value={userSearch}
                onChange={e => { setUserSearch(e.target.value); setUserPage(1); }}
                placeholder="Search username or email…"
                className="pl-8 pr-3 py-2 bg-zinc-800 border border-zinc-700/60 hover:border-zinc-600 focus:border-purple-500/60 rounded-xl text-xs text-zinc-200 placeholder-zinc-600 outline-none transition-colors w-60"
              />
            </div>
          </div>

          <UsersTable users={userList?.users || []} loading={userListLoading} />

          {/* Pagination */}
          {userList && userList.total > userList.perPage && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-800/40">
              <span className="text-xs text-zinc-500">
                Page {userPage} of {Math.ceil(userList.total / userList.perPage)}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setUserPage(p => Math.max(1, p - 1))}
                  disabled={userPage === 1}
                  className="px-3 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 rounded-lg transition-colors text-zinc-300"
                >
                  Prev
                </button>
                <button
                  onClick={() => setUserPage(p => p + 1)}
                  disabled={userPage >= Math.ceil(userList.total / userList.perPage)}
                  className="px-3 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 rounded-lg transition-colors text-zinc-300"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-xs text-zinc-700">
          Enumé Admin Dashboard &mdash; restricted access
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// DETAILED LINE CHARTS (SVG, no library)
// ============================================================================

const LineChart = ({ data = [], color = '#a855f7', label = '' }) => {
  if (!data.length) return <div className="h-32 flex items-center justify-center text-zinc-600 text-xs">No data</div>;

  const values = data.map(d => d.count);
  const max = Math.max(...values, 1);
  const min = 0;
  const range = max - min || 1;

  const W = 800;
  const H = 120;
  const padL = 32;
  const padR = 8;
  const padT = 8;
  const padB = 24;

  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  const pts = values.map((v, i) => {
    const x = padL + (i / Math.max(values.length - 1, 1)) * plotW;
    const y = padT + (1 - (v - min) / range) * plotH;
    return [x, y];
  });

  const line = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x} ${y}`).join(' ');
  const area = `M ${padL} ${padT + plotH} ` + pts.map(([x, y]) => `L ${x} ${y}`).join(' ') + ` L ${pts[pts.length - 1][0]} ${padT + plotH} Z`;

  // X axis labels — show ~6
  const step = Math.ceil(data.length / 6);
  const xLabels = data.filter((_, i) => i % step === 0 || i === data.length - 1);

  // Y axis grid
  const gridLines = [0, 0.25, 0.5, 0.75, 1].map(f => ({
    y: padT + (1 - f) * plotH,
    val: Math.round(min + f * range),
  }));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 128 }} preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id={`area-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {/* Grid lines */}
      {gridLines.map(({ y, val }) => (
        <g key={y}>
          <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#27272a" strokeWidth="1" />
          <text x={padL - 4} y={y + 3} textAnchor="end" fontSize="8" fill="#52525b">{val}</text>
        </g>
      ))}

      {/* Area fill */}
      <path d={area} fill={`url(#area-${color.replace('#', '')})`} />

      {/* Line */}
      <path d={line} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />

      {/* Data dots — only last point */}
      {pts.length > 0 && (
        <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="3" fill={color} />
      )}

      {/* X axis labels */}
      {xLabels.map((d, i) => {
        const idx = data.indexOf(d);
        const x = padL + (idx / Math.max(data.length - 1, 1)) * plotW;
        const shortDate = d.date?.slice(5); // MM-DD
        return (
          <text key={d.date} x={x} y={H - 4} textAnchor="middle" fontSize="8" fill="#52525b">
            {shortDate}
          </text>
        );
      })}
    </svg>
  );
};

const SignupLineChart = ({ data }) => <LineChart data={data} color="#a855f7" label="signups" />;
const DauLineChart    = ({ data }) => <LineChart data={data} color="#22c55e" label="DAU" />;

export default AdminDashboard;